import moment from 'moment'
import { hasSuccessfulDataPullSince } from './electronTelemetryTimelineHelpers'
import {
  isPepPermissionDeniedEvent,
  isSessionAuthPepDataPullFailure
} from './pepElectronOpsOperationalMessage'

export const PEP_LADDER_STATUS = {
  OK: 'ok',
  FAIL: 'fail',
  UNKNOWN: 'unknown'
}

function readBool(value) {
  if (value === true || value === 'true') {
    return true
  }
  if (value === false || value === 'false') {
    return false
  }
  return null
}

function primaryDevice(devices) {
  if (!Array.isArray(devices) || devices.length === 0) {
    return null
  }
  return [...devices].sort((a, b) => {
    const ta = moment(a.lastSeenAt ?? a.LastSeenAt)
    const tb = moment(b.lastSeenAt ?? b.LastSeenAt)
    return tb.valueOf() - ta.valueOf()
  })[0]
}

function latestEventMatching(events, predicate) {
  let latest = null
  for (const ev of events || []) {
    if (!predicate(ev)) {
      continue
    }
    const t = moment(ev.occurredAt ?? ev.OccurredAt)
    if (!t.isValid()) {
      continue
    }
    if (!latest || t.isAfter(moment(latest.occurredAt ?? latest.OccurredAt))) {
      latest = ev
    }
  }
  return latest
}

function eventTimeIso(ev) {
  if (!ev) {
    return null
  }
  const t = moment(ev.occurredAt ?? ev.OccurredAt)
  return t.isValid() ? t.toISOString() : null
}

function isDataPullOkEvent(ev) {
  const type = (ev.eventType ?? ev.EventType ?? '').toString().toLowerCase()
  const code = (ev.eventCode ?? ev.EventCode ?? '').toString()
  return type.includes('datapullsucceeded') || code === 'PEP_DATA_PULL_OK'
}

function isDataPullFailEvent(ev) {
  const code = (ev.eventCode ?? ev.EventCode ?? '').toString()
  return code.startsWith('PEP_DATA_PULL_FAIL')
}

/**
 * Builds an ordered, support-facing PEP "login ladder" so a CSM can read, at a glance,
 * which step the hotel desktop reached: dashboard sign-in -> Hilton PEP sign-in ->
 * session keep-alive -> PEP data pull. Each step reports ok / fail / unknown and the
 * most relevant timestamp. Pure function; safe to unit test without rendering.
 */
export function buildPepLoginLadder({ devices, timelineEvents } = {}) {
  const device = primaryDevice(devices)
  const events = timelineEvents || []

  const dashboardActive = device
    ? readBool(device.dashboardLoginActive ?? device.DashboardLoginActive)
    : null
  const pepActive = device
    ? readBool(device.pepLoginActive ?? device.PepLoginActive)
    : null

  const latestKeepAliveFail = latestEventMatching(
    events,
    ev => (ev.eventCode ?? ev.EventCode ?? '') === 'PEP_KEEP_ALIVE_FAILED'
  )
  const latestSessionAuthFail = latestEventMatching(
    events,
    ev =>
      (ev.eventCode ?? ev.EventCode ?? '') === 'PEP_KEEP_ALIVE_FAILED' ||
      isSessionAuthPepDataPullFailure(ev)
  )
  const latestPermissionDenied = latestEventMatching(events, isPepPermissionDeniedEvent)
  const latestSnoozed = latestEventMatching(
    events,
    ev => (ev.eventCode ?? ev.EventCode ?? '') === 'PEP_LOGIN_UI_SNOOZED'
  )
  const latestDataPullOk = latestEventMatching(events, isDataPullOkEvent)
  const latestDataPullFail = latestEventMatching(events, isDataPullFailEvent)
  const recentPullOk = hasSuccessfulDataPullSince(events, moment().subtract(48, 'hours'))

  const deviceLastSeen = device ? device.lastSeenAt ?? device.LastSeenAt ?? null : null

  let dashboardStatus = PEP_LADDER_STATUS.UNKNOWN
  if (device && dashboardActive === true) {
    dashboardStatus = PEP_LADDER_STATUS.OK
  } else if (device && dashboardActive === false) {
    dashboardStatus = PEP_LADDER_STATUS.FAIL
  }

  let pepStatus = PEP_LADDER_STATUS.UNKNOWN
  if (device && pepActive === true) {
    pepStatus = PEP_LADDER_STATUS.OK
  } else if (device && (pepActive === false || latestSessionAuthFail)) {
    pepStatus = PEP_LADDER_STATUS.FAIL
  }

  const snoozeIsNewest =
    latestSnoozed &&
    (!latestPermissionDenied ||
      moment(latestSnoozed.occurredAt ?? latestSnoozed.OccurredAt).isAfter(
        moment(latestPermissionDenied.occurredAt ?? latestPermissionDenied.OccurredAt)
      )) &&
    (!latestSessionAuthFail ||
      moment(latestSnoozed.occurredAt ?? latestSnoozed.OccurredAt).isAfter(
        moment(latestSessionAuthFail.occurredAt ?? latestSessionAuthFail.OccurredAt)
      ))

  let pepDetail = null
  if (pepStatus === PEP_LADDER_STATUS.FAIL) {
    if (latestPermissionDenied) {
      pepDetail = 'Permission denied by Hilton'
    } else if (snoozeIsNewest) {
      pepDetail = 'Sign-in postponed by user'
    } else {
      pepDetail = 'Needs sign-in'
    }
  }

  let sessionStatus = PEP_LADDER_STATUS.UNKNOWN
  if (latestKeepAliveFail) {
    sessionStatus = PEP_LADDER_STATUS.FAIL
  } else if (recentPullOk || latestDataPullOk) {
    sessionStatus = PEP_LADDER_STATUS.OK
  }

  let dataPullStatus = PEP_LADDER_STATUS.UNKNOWN
  let dataPullAt = eventTimeIso(latestDataPullOk)
  if (recentPullOk || latestDataPullOk) {
    const failNewerThanOk =
      latestDataPullFail &&
      latestDataPullOk &&
      moment(latestDataPullFail.occurredAt ?? latestDataPullFail.OccurredAt).isAfter(
        moment(latestDataPullOk.occurredAt ?? latestDataPullOk.OccurredAt)
      )
    if (failNewerThanOk) {
      dataPullStatus = PEP_LADDER_STATUS.FAIL
      dataPullAt = eventTimeIso(latestDataPullFail)
    } else {
      dataPullStatus = PEP_LADDER_STATUS.OK
    }
  } else if (latestDataPullFail) {
    dataPullStatus = PEP_LADDER_STATUS.FAIL
    dataPullAt = eventTimeIso(latestDataPullFail)
  }

  return [
    {
      key: 'dashboard',
      label: 'Dashboard sign-in',
      status: dashboardStatus,
      at: dashboardStatus === PEP_LADDER_STATUS.OK ? deviceLastSeen : null
    },
    {
      key: 'pepLogin',
      label: 'Hilton PEP sign-in',
      status: pepStatus,
      detail: pepDetail,
      at:
        pepStatus === PEP_LADDER_STATUS.FAIL
          ? eventTimeIso(latestPermissionDenied ?? latestSessionAuthFail)
          : pepStatus === PEP_LADDER_STATUS.OK
          ? deviceLastSeen
          : null
    },
    {
      key: 'session',
      label: 'Session keep-alive',
      status: sessionStatus,
      at:
        sessionStatus === PEP_LADDER_STATUS.FAIL
          ? eventTimeIso(latestKeepAliveFail)
          : sessionStatus === PEP_LADDER_STATUS.OK
          ? eventTimeIso(latestDataPullOk) ?? deviceLastSeen
          : null
    },
    {
      key: 'dataPull',
      label: 'PEP data pull',
      status: dataPullStatus,
      at: dataPullAt
    }
  ]
}
